import { useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { formatFileSize } from "../../../shared/lib/format-file-size";
import {
  FILE_SIZE_LIMIT_BYTES,
  FILE_SIZE_LIMIT_LABEL,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  useGetEventContentQuery,
  useUploadEventContentFileMutation,
  useCreateEventContentLinkMutation,
  useUpdateEventContentFileMutation,
  useUpdateEventContentLinkMutation,
  useDeleteEventContentMutation,
  AccessType,
  EventContentSourceType,
  getAccessTypeDisplayName,
  getEventContentSourceTypeDisplayName,
} from "../../../entities/admin-events";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";

interface EventContentManagementProps {
  eventId: number;
}

const getMutationErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "error" in error.data &&
    typeof error.data.error === "string"
  ) {
    return error.data.error;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const EventContentManagement = ({
  eventId,
}: EventContentManagementProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContentId, setEditingContentId] = useState<number | null>(null);
  const [editingSourceType, setEditingSourceType] =
    useState<EventContentSourceType | null>(null);
  const [createSourceType, setCreateSourceType] =
    useState<EventContentSourceType>(EventContentSourceType.FILE);
  const [contentFile, setContentFile] = useState<File | null>(null);
  const [contentUrl, setContentUrl] = useState("");
  const [contentTitle, setContentTitle] = useState("");
  const [contentDescription, setContentDescription] = useState("");
  const [accessTypeId, setAccessTypeId] = useState<AccessType | "">("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingContentId, setDeletingContentId] = useState<number | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const contentInputRef = useRef<HTMLInputElement>(null);

  const {
    data: eventContentData,
    refetch: refetchContent,
    isLoading: isLoadingContent,
  } = useGetEventContentQuery(eventId);
  const [uploadContentFile, { isLoading: isUploadingFile }] =
    useUploadEventContentFileMutation();
  const [createContentLink, { isLoading: isCreatingLink }] =
    useCreateEventContentLinkMutation();
  const [updateContentFile, { isLoading: isUpdatingFile }] =
    useUpdateEventContentFileMutation();
  const [updateContentLink, { isLoading: isUpdatingLink }] =
    useUpdateEventContentLinkMutation();
  const [deleteContent] = useDeleteEventContentMutation();

  const isDialogSaving =
    isUploadingFile || isCreatingLink || isUpdatingFile || isUpdatingLink;

  const isLinkMode =
    editingContentId !== null
      ? editingSourceType === EventContentSourceType.LINK
      : createSourceType === EventContentSourceType.LINK;

  const handleContentFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const sizeError = getFileSizeLimitError(
      file,
      FILE_SIZE_LIMIT_BYTES.CONTENT_AND_VIDEO,
    );
    if (sizeError) {
      setUploadError(sizeError);
      setContentFile(null);
      if (contentInputRef.current) {
        contentInputRef.current.value = "";
      }
      return;
    }

    setUploadError(null);
    setContentFile(file);
  };

  const resetUploadState = () => {
    setUploadProgress(0);
    setUploadError(null);
  };

  const resetForm = () => {
    setEditingContentId(null);
    setEditingSourceType(null);
    setCreateSourceType(EventContentSourceType.FILE);
    setContentTitle("");
    setContentDescription("");
    setContentUrl("");
    setContentFile(null);
    setAccessTypeId("");
    resetUploadState();
    if (contentInputRef.current) {
      contentInputRef.current.value = "";
    }
  };

  const handleOpenDialog = (contentId?: number) => {
    if (contentId) {
      const content = eventContentData?.items.find(
        (item) => item.id === contentId,
      );
      if (content) {
        setEditingContentId(contentId);
        setEditingSourceType(content.sourceTypeId);
        setContentTitle(content.title);
        setContentDescription(content.description);
        setContentUrl(content.url);
        setAccessTypeId(content.accessTypeId);
      }
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const buildMetadataBody = () => ({
    title: contentTitle,
    description: contentDescription,
    accessTypeId: accessTypeId as AccessType,
  });

  const handleSubmit = async () => {
    setUploadError(null);

    if (accessTypeId === "") {
      alert("Пожалуйста, выберите тип доступа");
      return;
    }

    try {
      if (editingContentId && editingSourceType !== null) {
        if (editingSourceType === EventContentSourceType.LINK) {
          if (!contentUrl) {
            alert("Пожалуйста, укажите URL ссылки");
            return;
          }
          await updateContentLink({
            id: eventId,
            contentId: editingContentId,
            body: {
              url: contentUrl,
              ...buildMetadataBody(),
            },
          }).unwrap();
        } else {
          await updateContentFile({
            id: eventId,
            contentId: editingContentId,
            body: buildMetadataBody(),
          }).unwrap();
        }
      } else if (isLinkMode) {
        if (!contentUrl) {
          alert("Пожалуйста, укажите URL ссылки");
          return;
        }
        if (!contentTitle) {
          alert("Пожалуйста, укажите название контента");
          return;
        }

        await createContentLink({
          id: eventId,
          body: {
            url: contentUrl,
            ...buildMetadataBody(),
          },
        }).unwrap();
      } else {
        if (!contentFile) {
          alert("Пожалуйста, выберите файл для загрузки");
          return;
        }

        const formData = new FormData();
        formData.append("file", contentFile);

        resetUploadState();

        const uploadResult = await uploadContentFile({
          id: eventId,
          file: formData,
          onProgress: setUploadProgress,
        }).unwrap();
        setUploadProgress(100);

        if (uploadResult.result && uploadResult.content) {
          await updateContentFile({
            id: eventId,
            contentId: uploadResult.content.id,
            body: {
              title: contentTitle || uploadResult.content.title,
              description:
                contentDescription || uploadResult.content.description,
              accessTypeId,
            },
          }).unwrap();
        }
      }

      refetchContent();
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to upload/update content:", error);
      setUploadError(
        getMutationErrorMessage(error, "Не удалось загрузить/обновить контент"),
      );
      resetUploadState();
    }
  };

  const handleDelete = async (contentId: number) => {
    if (!confirm("Вы уверены, что хотите удалить этот контент?")) {
      return;
    }

    try {
      setDeletingContentId(contentId);
      await deleteContent({
        id: eventId,
        contentId,
      }).unwrap();
      refetchContent();
    } catch (error) {
      console.error("Failed to delete content:", error);
      alert(getMutationErrorMessage(error, "Не удалось удалить контент"));
    } finally {
      setDeletingContentId(null);
    }
  };

  if (isLoadingContent) {
    return <LoadingIndicator />;
  }

  const isSubmitDisabled =
    isDialogSaving ||
    accessTypeId === "" ||
    (editingContentId === null &&
      isLinkMode &&
      (!contentUrl || !contentTitle)) ||
    (editingContentId === null && !isLinkMode && !contentFile);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Контент мероприятия</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={isDialogSaving}
        >
          Добавить контент
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Тип</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Тип доступа</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Источник</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!eventContentData?.items || eventContentData.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Нет контента
                </TableCell>
              </TableRow>
            ) : (
              eventContentData.items.map((content) => (
                <TableRow key={content.id}>
                  <TableCell>
                    {getEventContentSourceTypeDisplayName(content.sourceTypeId)}
                  </TableCell>
                  <TableCell>{content.title}</TableCell>
                  <TableCell>{content.description}</TableCell>
                  <TableCell>
                    {getAccessTypeDisplayName(content.accessTypeId)}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(content.createdAt), "dd.MM.yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    {content.sourceTypeId === EventContentSourceType.FILE ? (
                      <Button
                        variant="text"
                        href={content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {content.file?.name || "Скачать"}
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        href={content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Открыть ссылку
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(content.id)}
                        title="Редактировать"
                        disabled={isDialogSaving}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(content.id)}
                        title="Удалить"
                        disabled={deletingContentId !== null || isDialogSaving}
                      >
                        {deletingContentId === content.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={isDialogOpen}
        onClose={() => {
          if (!isDialogSaving) {
            handleCloseDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ position: "relative" }}>
          {isDialogSaving && !isUploadingFile && <LoadingIndicator />}
          <DialogTitle>
            {editingContentId ? "Редактировать контент" : "Добавить контент"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {isUploadingFile && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Загрузка файла: {uploadProgress}%
                    {contentFile ? ` (${contentFile.name})` : ""}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                </Box>
              )}

            {uploadError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {uploadError}
              </Typography>
            )}

            {!editingContentId && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Тип контента
                </Typography>
                <ToggleButtonGroup
                  value={createSourceType}
                  exclusive
                  fullWidth
                  onChange={(_, value) => {
                    if (value !== null) {
                      setCreateSourceType(value);
                      setContentFile(null);
                      setContentUrl("");
                      if (contentInputRef.current) {
                        contentInputRef.current.value = "";
                      }
                    }
                  }}
                >
                  <ToggleButton value={EventContentSourceType.FILE}>
                    Файл
                  </ToggleButton>
                  <ToggleButton value={EventContentSourceType.LINK}>
                    Внешняя ссылка
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {editingContentId && editingSourceType !== null && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Тип: {getEventContentSourceTypeDisplayName(editingSourceType)}
              </Typography>
            )}

            {(isLinkMode ||
              editingSourceType === EventContentSourceType.LINK) && (
              <TextField
                fullWidth
                label="URL"
                required
                value={contentUrl}
                onChange={(e) => setContentUrl(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="https://..."
              />
            )}

            <TextField
              fullWidth
              label="Название"
              required={!editingContentId && isLinkMode}
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              sx={{ mb: 2 }}
              helperText={
                !editingContentId && !isLinkMode
                  ? "Необязательно для нового файла"
                  : ""
              }
            />

            <TextField
              fullWidth
              label="Описание"
              multiline
              rows={3}
              value={contentDescription}
              onChange={(e) => setContentDescription(e.target.value)}
              sx={{ mb: 2 }}
              helperText={
                !editingContentId && !isLinkMode
                  ? "Необязательно для нового файла"
                  : ""
              }
            />

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel shrink>Тип доступа</InputLabel>
              <Select
                value={accessTypeId}
                displayEmpty
                onChange={(e) =>
                  setAccessTypeId(e.target.value as AccessType | "")
                }
                label="Тип доступа"
                renderValue={() => {
                  if (accessTypeId === "") {
                    return (
                      <Typography component="span" color="text.secondary">
                        Выберите тип доступа
                      </Typography>
                    );
                  }

                  return getAccessTypeDisplayName(accessTypeId);
                }}
              >
                <MenuItem value="" disabled>
                  <em>Выберите тип доступа</em>
                </MenuItem>
                <MenuItem value={AccessType.CLOSED}>
                  {getAccessTypeDisplayName(AccessType.CLOSED)}
                </MenuItem>
                <MenuItem value={AccessType.USERS_AND_RESIDENTS}>
                  {getAccessTypeDisplayName(AccessType.USERS_AND_RESIDENTS)}
                </MenuItem>
                <MenuItem value={AccessType.RESIDENTS_ON_EVENT_DATE}>
                  {getAccessTypeDisplayName(AccessType.RESIDENTS_ON_EVENT_DATE)}
                </MenuItem>
                <MenuItem value={AccessType.RESIDENTS_ON_VIEW}>
                  {getAccessTypeDisplayName(AccessType.RESIDENTS_ON_VIEW)}
                </MenuItem>
              </Select>
            </FormControl>

            {!editingContentId && !isLinkMode && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Файл контента
                </Typography>
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleContentFileChange}
                  ref={contentInputRef}
                  id="content-upload"
                />
                <label htmlFor="content-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Выбрать файл
                  </Button>
                </label>
                <FileUploadInfo
                  type="content"
                  maxSize={FILE_SIZE_LIMIT_LABEL.CONTENT_AND_VIDEO}
                  recommendedSize="Зависит от типа файла"
                />
                {contentFile && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Выбран файл: {contentFile.name} (
                      {formatFileSize(contentFile.size)})
                    </Typography>
                    {isUploadingFile && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Загрузка: {uploadProgress}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isDialogSaving}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitDisabled}
          >
            {isUploadingFile
              ? `Загрузка ${uploadProgress}%`
              : editingContentId
                ? "Сохранить"
                : isLinkMode
                  ? "Добавить ссылку"
                  : "Загрузить"}
          </Button>
        </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};
